import { useCallback, useEffect, useMemo } from 'react';
import { useActiveSpeakerState } from 'hooks/useActiveSpeakerState';
import { useVideoPipManager } from 'hooks/useVideoPipManager';
import { PipEntryPoint } from 'utils/pip/types';

export const useVideoPip = () => {
  const videoPipManager = useVideoPipManager();

  const { speaker, active, audioEnabled, hasNoStreams } = useActiveSpeakerState();

  const settings = useMemo(
    () => ({
      showPlaceholder: hasNoStreams,
      muted: !audioEnabled,
      active,
    }),
    [hasNoStreams, audioEnabled, active]
  );

  const requestVideoPipWindow = useCallback(
    async (
      width: number,
      height: number,
      entry: PipEntryPoint
    ): Promise<HTMLVideoElement | null> => {
      if (!speaker) {
        return null;
      }

      return videoPipManager.requestWindow(width, height, speaker, settings, entry);
    },
    [videoPipManager, speaker, settings]
  );

  const prepareVideoPipWindow = useCallback(
    (width: number, height: number) => {
      if (!speaker) {
        return;
      }

      videoPipManager.prepare(width, height, speaker, settings);
    },
    [videoPipManager, speaker, settings]
  );

  const closeVideoPipWindow = useCallback(() => videoPipManager.stop(), [videoPipManager]);

  const getVideoPipTelemetry = useCallback(
    () => videoPipManager.telemetrySnapshot(),
    [videoPipManager]
  );

  useEffect(() => {
    if (!videoPipManager.isActive() || !speaker) {
      return;
    }

    videoPipManager.redraw(speaker, settings);
  }, [videoPipManager, speaker, settings]);

  return {
    requestVideoPipWindow,
    prepareVideoPipWindow,
    closeVideoPipWindow,
    getVideoPipTelemetry,
  };
};
